import * as React from 'react';
import { BCCascader, BCTreeSelectEntities } from '../../..';
import { IPropertyComponentProps } from '../../entities';

const inputStyle: React.CSSProperties = { width: '100%' };

export default function CascaderProperty(props: IPropertyComponentProps) {
	const countRef = React.useRef<number>(0);
	const { items, multiple, dropdownRender, isCustomCascader, optionRender } =
		props.item.externalData;

	const onValueChanged = (value: any) => {
		props.onValueChanged(props.item, value);
	};

	React.useEffect(() => {
		if (typeof props.value === 'string' && props.value) {
			if (props.value.includes(':')) {
				const [_, childValuesStr] = props.value.split(':');

				const childValues = childValuesStr.replace('[{', '').replace('}]', '').split(',');

				countRef.current = childValues.length;
			} else {
				countRef.current = 1;
			}
		}
	}, []);

	const onChangeMultiple = (_: string[][], selectedItems: BCTreeSelectEntities.ITreeData[][]) => {
		if (!selectedItems.length) {
			countRef.current = 0;
			return onValueChanged('');
		}

		const lastSelected = selectedItems.at(-1);
		const self = lastSelected.at(-1);
		const parent = lastSelected.at(-2);

		if (props.item.externalData?.isCustomCascader) {
			const cascaderString: string[] = [];
			for (const item of selectedItems) {
				switch (item.length) {
					case 1:
						item[0].children.forEach(({ children }) =>
							children.forEach(({ value }) => cascaderString.push(value))
						);
						break;
					case 2:
						item[1].children.forEach(({ value }) => cascaderString.push(value));
						break;
					case 3:
						cascaderString.push(item[2].value);
						break;
				}
			}
			return onValueChanged(cascaderString);
		}

		if (parent.externalData?.isArray) {
			if (selectedItems.length >= countRef.current) {
				// insert
				if (typeof props.value === 'string') {
					if (props.value.includes(':')) {
						const [oldParentValue, oldChildValuesStr] = props.value.split(':');

						if (oldParentValue !== parent.value) {
							countRef.current = 1;
							return onValueChanged(`${parent.value}:[{${self.externalData.name}}]`);
						}

						const oldChildValuesClean = oldChildValuesStr
							.replace('[{', '')
							.replace('}]', '');

						countRef.current = oldChildValuesClean.split(',').length + 1;
						return onValueChanged(
							`${parent.value}:[{${oldChildValuesClean}, ${self.externalData.name}}]`
						);
					} else {
						countRef.current = 1;
						return onValueChanged(`${parent.value}:[{${self.externalData.name}}]`);
					}
				}
				// delete
			} else if (typeof props.value === 'string' && props.value.includes(':')) {
				const [oldParentValue, oldChildValuesStr] = props.value.split(':');
				const oldChildValuesArr = oldChildValuesStr
					.replace('[{', '')
					.replace('}]', '')
					.split(',')
					.map(path => path.trim());

				const newChildValuesArr = selectedItems
					.map(item => {
						const title = item.at(-1).externalData.name;
						if (oldChildValuesArr.includes(title)) {
							return title;
						}
					})
					.filter(Boolean);

				countRef.current = newChildValuesArr.length;
				return onValueChanged(`${oldParentValue}:[{${newChildValuesArr.join(', ')}}]`);
			}
		} else {
			countRef.current = 1;
			return onValueChanged(self.value);
		}
	};

	const onChangeSingle = (_: string[], selectedItems: BCTreeSelectEntities.ITreeData[]) => {
		const lastSelected = selectedItems?.at(-1);
		return onValueChanged(lastSelected);
	};

	const getValue = () => {
		if (props.value?.length && isCustomCascader) {
			return props.value?.map((e: string) =>
				e.split('.').map((_, index, self) => self.slice(0, index + 1).join('.'))
			);
		}
		if (typeof props.value === 'string' && props.value) {
			if (props.value.includes(':')) {
				const [parentValue, childValuesStr] = props.value.split(':');

				const childValues = childValuesStr
					.replace('[{', '')
					.replace('}]', '')
					.split(',')
					.map(path => path.trim());

				return childValues
					.map(path => `${parentValue}.${path}`)
					.map(path =>
						path.split('.').map((_path, i, self) => {
							const others = self.slice(0, i).join('.');
							return others.length ? `${others}.${_path}` : _path;
						})
					);
			} else {
				const arr = props.value.split('.');
				return arr.map((val, i, self) => {
					const others = self.slice(0, i).join('.');
					return others.length ? `${others}.${val}` : val;
				});
			}
		}
	};

	const displayRender = React.useCallback((_, selectedOptions) => {
		return <span title={selectedOptions?.at(-1)?.value}>{selectedOptions?.at(-1)?.value}</span>;
	}, []);

	return (
		<BCCascader
			size="small"
			disabled={props.item.readOnly}
			options={items}
			multiple={multiple}
			dropdownRender={dropdownRender}
			displayRender={displayRender}
			onChange={multiple ? onChangeMultiple : onChangeSingle}
			value={getValue()}
			style={inputStyle}
			showSearch
			optionRender={optionRender}
		/>
	);
}
